import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import { Modal as antModal } from 'antd';
import Pagination from 'component/pagination';
import SectionTitle from 'component/section-title/sectionTitle';
import { useParams } from 'react-router-dom';
import { MY_EVENTS_ROUTE } from 'routes/events/list';
import { useDebounce } from 'tools/hooks/debounce.hook';
import { Button, Input, Label, Link, Table } from 'ui';

import {
  useLazyExportEventFollowersQuery,
  useLazyGetEventFollowersQuery,
} from 'services/events/eventsApiService';
import { EventFollower } from 'services/events/evetns.api.type';

import s from './EventFollowers.module.scss';
import {
  EVENT_FOLLOWERS_SORTBY_ENUM,
  FollowerFilterType,
  initialFilter,
} from './EventFollowers.utils';

const { confirm } = antModal;

const EventFollowers: FC = () => {
  const [exportEventFollowers] = useLazyExportEventFollowersQuery();
  const { eventId } = useParams();
  const [getEventFollowers, { data: followers, isFetching: followerFetching }] =
    useLazyGetEventFollowersQuery();
  const [requestFilter, setRequestFilter] = useState<FollowerFilterType>(initialFilter);
  const [filterEmail, setFilterEmail] = useState<string>('');
  const debouncedSearchEventName = useDebounce(filterEmail, 500) as string;
  const [tableData, setTableData] = useState<EventFollower[]>([]);
  const [sortBy, setSortBy] = useState<EVENT_FOLLOWERS_SORTBY_ENUM>(
    EVENT_FOLLOWERS_SORTBY_ENUM.EMAIL,
  );
  const [isDownLoading, setIsDownLoading] = useState<boolean>();

  const showedElements =
    Number(followers?.subscribers?.length) +
    (Number(followers?.pageNumber) - 1) * Number(requestFilter.PageSize);

  const handlePageChange = useCallback((PageNumber: number) => {
    setRequestFilter((prevState) => ({
      ...prevState,
      PageNumber,
    }));
  }, []);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFilterEmail(event.target.value);
  };

  useEffect(() => {
    if (sortBy) {
      setRequestFilter((prevState) => ({
        ...prevState,
        sortBy: sortBy,
      }));
    }
  }, [sortBy]);

  useEffect(() => {
    if (debouncedSearchEventName?.length >= 3) {
      setRequestFilter((prevState) => ({
        ...prevState,
        Term: debouncedSearchEventName,
      }));
    } else if (debouncedSearchEventName?.length === 0) {
      setRequestFilter((prevState) => ({
        ...prevState,
        Term: '',
      }));
    }
  }, [debouncedSearchEventName]);

  useEffect(() => {
    if (eventId) {
      const updatedFilter = {
        ...requestFilter,
        id: eventId,
      };
      getEventFollowers(updatedFilter);
    }
  }, [requestFilter, eventId, getEventFollowers]);

  const showExportConfirm = async () => {
    confirm({
      title: 'Would you like to export the followers of this event?',
      icon: <InfoCircleOutlined />,
      content: '',
      centered: true,
      mask: true,
      async onOk() {
        setIsDownLoading(true);
        if (eventId) {
          exportEventFollowers({ id: eventId }).then((data) => {
            if (data.data) {
              const link = document.createElement('a');
              link.href = data.data?.fileName;
              link.setAttribute('download', `Followers-${eventId}.csv`);
              link.style.display = 'none';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              setIsDownLoading(false);
            }
          });
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const handleExport = () => {
    showExportConfirm();
  };

  useEffect(() => {
    setTableData(followers?.subscribers ?? []);
  }, [setTableData, followers]);

  const columns = [
    {
      title: 'Profile',
      dataIndex: 'profilePic',
      key: 'profilePic',
      render: (url: string, record) => {
        return url ? (
          <img
            src={url}
            alt="Profile"
            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: '50%' }}
          />
        ) : (
          <div className={s.logo}>{record.firstName?.[0] ?? '?'}</div>
        );
      },
    },
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
      render: (follower) => `${follower}`,
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
      render: (follower) => `${follower}`,
    },
    {
      title: 'E-MAIL',
      dataIndex: 'email',
      key: 'email',
      render: (follower) => `${follower}`,
    },
    {
      title: 'Mobile',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (follower) => `${follower}`,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (value: string) =>
        new Date(value).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
  ];

  return (
    <div className={s.wrapper}>
      <Link
        to={MY_EVENTS_ROUTE}
        icon="arrowLeft"
        label="My Events"
        labelType="h6"
        className={s.backLink}></Link>
      <div className={s.row}>
        <SectionTitle label="common_event_followers" className={s.title} />
        <div className={s.titleButton}>
          <Button
            size="large"
            style={{ fontSize: 13 }}
            icon="download"
            loading={isDownLoading}
            type="primary"
            onClick={handleExport}>
            EXPORT LIST
          </Button>
        </div>
      </div>
      <div className={s.content}>
        <div className={s.notificationList}>
          <div className={s.followerSearch}>
            <Input
              name="email"
              fullWidth
              placeholder="Name, E-mail or Mobile"
              value={filterEmail}
              onChange={handleSearchChange}
            />
            <Select
              style={{ height: '100%', width: '240px', backgroundColor: 'white' }}
              onChange={(value) => setSortBy(value)}
              options={[
                { value: EVENT_FOLLOWERS_SORTBY_ENUM.NAME, label: 'Name' },
                { value: EVENT_FOLLOWERS_SORTBY_ENUM.EMAIL, label: 'E-mail' },
                { value: EVENT_FOLLOWERS_SORTBY_ENUM.DATE, label: 'Date' },
              ]}
              defaultValue={sortBy}
            />
          </div>
          <Label>
            A list of all your event subscribers can be found below. These subscribers would have
            for example, subscribed for pre-event sales, or for when tickets will come back on sale.
          </Label>
          <Table
            dataSource={tableData}
            loading={followerFetching}
            columns={columns}
            pagination={false}
            scroll={{ x: 768 }}
            rowKey="orderId"
          />
          {showedElements > 0 && (
            <Pagination
              totalElements={followers?.totalRecords}
              showedElements={showedElements}
              currentPage={requestFilter?.PageNumber || 0}
              total={followers?.totalPages}
              handlePageChange={handlePageChange}
              handleSizeChange={(size) =>
                setRequestFilter((prevValue) => ({ ...prevValue, PageSize: size }))
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EventFollowers;
